// InviteLanding — splash page keyed by token.
// Shows pack title, blurb (serif), voice_note (small, muted), and a Begin CTA
// that routes to the first question in the pack.

import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'wouter'
import {
  getInvitation,
  getStoredLang,
  setStoredLang,
  type Lang,
  type Pack,
} from '@/lib/api'
import { flattenQuestions } from '@/lib/pack'
import { Button } from '@/components/ui/button'
import { Markdown } from '@/components/Markdown'
import { LangSwitcher } from '@/components/LangSwitcher'
import { t } from '@/lib/i18n'

export function InviteLanding() {
  const params = useParams() as { token: string }
  const token = params.token
  const [, setLocation] = useLocation()
  const [pack, setPack] = useState<Pack | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [lang, setLang] = useState<Lang>(() => getStoredLang())
  const [translating, setTranslating] = useState(false)

  useEffect(() => {
    let cancelled = false
    // Only show the "Translating…" modal if the fetch takes long enough to
    // notice. Cached translations return in <200ms — no need to flash a modal.
    let modalTimer: ReturnType<typeof setTimeout> | null = null
    if (lang !== 'en') {
      modalTimer = setTimeout(() => {
        if (!cancelled) setTranslating(true)
      }, 500)
    }
    getInvitation(token, lang)
      .then((res) => {
        if (cancelled) return
        if (modalTimer) clearTimeout(modalTimer)
        setPack(res.pack)
        setStatus(res.invitation.status)
        setTranslating(false)
      })
      .catch((err) => {
        if (cancelled) return
        if (modalTimer) clearTimeout(modalTimer)
        setError(err?.message === 'not_found' ? 'not_found' : 'load_failed')
        setTranslating(false)
      })
    return () => {
      cancelled = true
      if (modalTimer) clearTimeout(modalTimer)
    }
  }, [token, lang])

  function onChangeLang(next: Lang) {
    setStoredLang(next)
    setLang(next)
  }

  if (error === 'not_found') {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
        <div className="max-w-lg text-center space-y-4">
          <h1 className="font-serif text-3xl sm:text-4xl">{t(lang, 'invalid_link')}</h1>
          <p className="font-serif text-muted-foreground">
            {t(lang, 'invalid_link_body')}
          </p>
        </div>
      </main>
    )
  }

  if (error) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
        <p className="font-serif text-muted-foreground">{t(lang, 'load_failed_generic')}</p>
      </main>
    )
  }

  if (!pack) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
        <p className="font-serif text-muted-foreground">{t(lang, 'loading')}</p>
      </main>
    )
  }

  const flat = flattenQuestions(pack)
  const first = flat[0]

  function begin() {
    if (status === 'completed') {
      setLocation(`/i/${token}/done`)
      return
    }
    if (!first) return
    setLocation(
      `/i/${token}/q/${encodeURIComponent(first.sectionKey)}/${encodeURIComponent(first.question.id)}`,
    )
  }

  return (
    <main className="min-h-screen bg-cream px-6 py-10 sm:py-16 relative">
      {translating && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-cream/80 backdrop-blur-sm"
          role="dialog"
          aria-live="assertive"
          aria-busy="true"
        >
          <div className="rounded-md border border-vermillion/30 bg-white shadow-lg px-6 py-5 text-center space-y-2">
            <div className="flex items-center justify-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-vermillion animate-pulse" />
              <span
                className="inline-block h-2 w-2 rounded-full bg-vermillion animate-pulse"
                style={{ animationDelay: '150ms' }}
              />
              <span
                className="inline-block h-2 w-2 rounded-full bg-vermillion animate-pulse"
                style={{ animationDelay: '300ms' }}
              />
            </div>
            <p className="font-serif text-base text-foreground">
              {t(lang, 'translating')}
            </p>
            <p className="text-xs text-muted-foreground">
              ~5 sec · cached after that
            </p>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-xl space-y-8">
        <div className="flex justify-center">
          <img
            src="/author.jpg"
            alt=""
            aria-hidden="true"
            className="w-40 h-40 sm:w-56 sm:h-56 object-cover rounded-full border border-black/10 shadow-sm"
          />
        </div>

        <header className="space-y-3 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Cows, <span className="strike-men">Men</span>, Paddocks — Interviews
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
            {pack.title}
          </h1>
          <div className="flex justify-center pt-1">
            <LangSwitcher value={lang} onChange={onChangeLang} />
          </div>
        </header>

        <Markdown
          className="font-serif text-lg sm:text-xl leading-relaxed space-y-4 text-foreground/90 markdown-body"
        >
          {pack.blurb}
        </Markdown>

        {pack.voice_note && (
          <Markdown
            className="text-sm text-muted-foreground border-l-2 border-vermillion/50 pl-4 italic markdown-body"
          >
            {pack.voice_note}
          </Markdown>
        )}

        <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            {t(lang, 'landing_meta', { n: flat.length })}
          </p>
          <Button
            size="lg"
            onClick={begin}
            disabled={translating}
            className="bg-vermillion hover:bg-vermillion/90 text-white w-full sm:w-auto disabled:opacity-60"
          >
            {status === 'completed'
              ? t(lang, 'begin_done')
              : status === 'in_progress' || status === 'opened'
              ? t(lang, 'begin_continue')
              : t(lang, 'begin')}
          </Button>
        </div>
      </div>
    </main>
  )
}

export default InviteLanding
